import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowLeft, CheckCircle, Award, Globe, Shield, Sun, Zap } from 'lucide-react';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import { Button } from '../../components/ui/button';

const JinkoSolarPage = () => {
  const features = [
    {
      icon: Globe,
      title: "World's #1 Manufacturer",
      description: "JinkoSolar is the world's largest solar module manufacturer, trusted globally for quality and reliability."
    },
    {
      icon: Award,
      title: "Top Performer",
      description: "Recognised as Top Performer in PVEL/DNV GL PV Module Reliability Scorecard for multiple consecutive years."
    },
    {
      icon: Shield,
      title: "Industry-Leading Warranty",
      description: "Backed by a 3rd party underwriter warranty, giving you extra peace of mind for your investment."
    },
    {
      icon: Sun,
      title: "Australian Support",
      description: "Wholly-owned Australian subsidiary providing local sales, customer service, and technical support."
    }
  ];

  const applications = [
    { name: "Residential", description: "Perfect for homes looking to reduce electricity bills and go solar." },
    { name: "Commercial", description: "Scalable solutions for businesses of all sizes." },
    { name: "Utility", description: "Large-scale projects for maximum energy generation." }
  ];

  const certifications = [
    "Top Brand PV Australia - EuPD Research",
    "PVEL/DNV GL Top Performer",
    "Intersolar Award Winner",
    "CEC Approved"
  ];

  return (
    <div className="min-h-screen">
      <Header />
      <main>
        {/* Hero Section */}
        <section className="pt-32 pb-16 bg-gradient-to-br from-[#f8faf5] via-white to-[#f0f7e6] relative overflow-hidden">
          <div className="absolute top-20 right-10 w-72 h-72 bg-[#B5D43B]/10 rounded-full blur-3xl" />
          <div className="absolute bottom-20 left-10 w-96 h-96 bg-[#3D5A47]/5 rounded-full blur-3xl" />
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            {/* Breadcrumb */}
            <Link to="/brands" className="inline-flex items-center gap-2 text-[#3D5A47] hover:text-[#B5D43B] mb-8 transition-colors">
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm font-medium">Back to Trusted Brands</span>
            </Link>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <Sun className="w-5 h-5 text-[#B5D43B]" />
                  <span className="text-sm font-semibold text-[#B5D43B]">Solar Panels</span>
                </div>
                <h1 className="heading-1 mb-4">
                  <span className="text-[#B5D43B]">Jinko</span>Solar
                </h1>
                <p className="text-xl text-[#3D5A47] font-medium mb-4">
                  Australia's Favourite Solar Panel
                </p>
                <p className="body-large text-gray-600 mb-8">
                  JinkoSolar (NYSE: JKS) is a global leader in the solar industry and the world's largest solar module manufacturer. With a commitment to manufacturing excellence and local Australian support, Jinko delivers quality, reliability and unrivalled performance.
                </p>
                <div className="flex flex-wrap gap-4">
                  <Link to="/contact">
                    <Button className="btn-primary gap-2">
                      Get a Quote with Jinko
                      <ArrowRight className="w-4 h-4" />
                    </Button>
                  </Link>
                  <a href="https://jinkosolar.com.au/" target="_blank" rel="noopener noreferrer">
                    <Button className="btn-secondary gap-2">
                      Visit Jinko Website
                      <Globe className="w-4 h-4" />
                    </Button>
                  </a>
                </div>
              </div>
              <div className="flex justify-center">
                <div className="bg-white p-8 rounded-2xl shadow-xl border border-gray-100">
                  <img 
                    src="https://jinkosolar.com.au/wp-content/themes/jinkosolar/images/logo.svg"
                    alt="JinkoSolar Logo"
                    className="w-64 h-auto"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "https://ui-avatars.com/api/?name=Jinko+Solar&background=B5D43B&color=3D5A47&size=256";
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Key Features */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="heading-2 text-center mb-12">Why Choose JinkoSolar?</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {features.map((feature, index) => (
                <div key={index} className="bg-gray-50 p-6 rounded-2xl hover:shadow-lg transition-shadow">
                  <div className="w-12 h-12 rounded-xl bg-[#B5D43B]/15 flex items-center justify-center mb-4">
                    <feature.icon className="w-6 h-6 text-[#3D5A47]" />
                  </div>
                  <h3 className="font-semibold text-[#3D5A47] mb-2">{feature.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* About Jinko */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="heading-2 mb-6">What JinkoSolar Does</h2>
                <p className="text-gray-600 leading-relaxed mb-4">
                  JinkoSolar distributes its solar products and sells its solutions and services to diversified international utility, commercial and residential customers.
                </p>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Manufacturing excellence is their highest priority, as underpinned by the 3rd party underwriter of their industry leading warranty. Their wholly-owned Australian subsidiary provides JinkoSolar products and first class service locally, from sales, customer service, supply chain management, to technical and warranty support.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  JinkoSolar invests heavily in R&D, consistently pushing the boundaries of solar technology to deliver the most efficient and reliable panels on the market.
                </p>
              </div>
              <div className="space-y-4">
                <h3 className="font-semibold text-[#3D5A47] mb-4">Certifications & Awards</h3>
                {certifications.map((cert, index) => (
                  <div key={index} className="flex items-center gap-3 p-4 bg-white rounded-xl border border-gray-100">
                    <CheckCircle className="w-5 h-5 text-[#B5D43B] flex-shrink-0" />
                    <span className="text-gray-700">{cert}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Applications */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="heading-2 text-center mb-12">Applications</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {applications.map((app, index) => (
                <div key={index} className="bg-gradient-to-br from-[#3D5A47] to-[#2d4435] p-6 rounded-2xl text-white">
                  <h3 className="font-semibold text-xl mb-2">{app.name}</h3>
                  <p className="text-white/80">{app.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 bg-[#B5D43B]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="heading-2 text-[#3D5A47] mb-4">Ready to Go Solar with Jinko?</h2>
            <p className="text-[#3D5A47]/80 mb-8 max-w-2xl mx-auto">
              As an authorised JinkoSolar installer, we can help you harness the power of the world's most trusted solar panels.
            </p>
            <Link to="/contact">
              <Button className="bg-[#3D5A47] hover:bg-[#2d4435] text-white gap-2">
                Get Your Free Quote
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default JinkoSolarPage;
